/**
 * Domain events — the closed list of things that happen in this system and turn
 * into work.
 *
 * `ARCHITECTURE.md` §The task queue: "Where tasks come from: people, through the
 * studio or MCP, and `task_rule`s fired by domain events inside the same
 * transaction." The nine rows of that table are the nine events here, and each one
 * carries a payload schema saying exactly what the session that raises it has to
 * pass — because a rule's title is a template over those fields, and a field nobody
 * agreed on is a `{{placeholder}}` in a task title six months later.
 *
 * **Names are dotted**: `work_order.approved`. An action is `tasks_complete` and a
 * job is `mail.send`; an event is neither, and the dot is what stops it being read as
 * either. The list is closed and append-only — sessions 11 to 17 add the events they
 * raise, with a line in their report.
 *
 * The payloads are all **flat and printable**, for the same reason a tool input is:
 * the rule engine substitutes `{{field}}` into a title, and the only values that can
 * be substituted are ones with an obvious rendering. A string, a whole number, or an
 * amount of money — nothing nested, nothing that needs a decision to print.
 */
import { z } from 'zod';
import { MoneySchema, PeriodSchema, Rfc3339Schema } from './primitives.js';
import { ClientSlugSchema } from './refs.js';
/**
 * Every event, in the order `ARCHITECTURE.md` §The task queue lists them.
 *
 * The session that will raise each one is named beside it, so a session reading this
 * list knows which rows are its own to call `taskRules.fire` for.
 */
export const DOMAIN_EVENTS = [
    /** A client approved a quote. Session 11. */
    'work_order.approved',
    /** A quote has been sitting unanswered for seven days. Session 11. */
    'work_order.quote_unanswered',
    /** The last retry on an invoice failed. Session 15. */
    'payment.final_attempt_failed',
    /** A retainer has been past due for fourteen days. Session 12. */
    'retainer.past_due',
    /** A monthly report is due in seven days. Session 16. */
    'report.due_soon',
    /** A deliverable has been waiting for the client's sign-off for five days. Session 16. */
    'deliverable.awaiting_sign_off',
    /** A widget reached its cap for the period. Session 13. */
    'widget.cap_reached',
    /** A Wise credit landed that no invoice claims. Session 15. */
    'payment.credit_unmatched',
    /** A client's effective rate fell under the floor for a period. Session 17. */
    'analytics.effective_rate_below_floor',
    /*
     * Session 11's five, appended in the order a work order meets them.
     *
     * Four of them have **no seeded rule**, and that is deliberate rather than an
     * oversight: `ARCHITECTURE.md` §The task queue's table has nine rows and none of
     * these is one of them, and a task per quote sent, per quote declined and per
     * milestone delivered is a board nobody would read. They exist because a domain
     * event is also how one module tells another something happened without importing
     * it — 14 is what subscribes to `work_order.approved_project` and
     * `milestone.delivered`, and until it does `taskRules.fire` finds no rule and
     * does nothing, which is the whole of "recorded and nothing else happens".
     * `seed/task-rules.unit.test.ts` pins the list of un-ruled events, so a **tenth**
     * event that should have had a rule still fails a test.
     */
    /** Staff or an agent sent a priced quote to a client. Session 11. */
    'work_order.proposed',
    /** The client said no. Session 11. */
    'work_order.declined',
    /** An extra or a project was delivered, and is waiting to be billed. Session 11. */
    'work_order.delivered',
    /**
     * A **project** was approved and its deposit is due. Session 14 issues the invoice.
     *
     * Separate from `work_order.approved` — which fires for every kind and raises the
     * "Start WO-…" task — because only this one carries the milestone and the amount a
     * deposit invoice is drawn from, and only projects with `deposit_on_approval` fire
     * it at all.
     */
    'work_order.approved_project',
    /** A project milestone was delivered; its invoice is due. Session 14. */
    'milestone.delivered',
    /*
     * Session 12's, in the order a retainer meets them.
     *
     * `retainer.past_due` above is already the fourteen-day row of
     * `ARCHITECTURE.md` §The task queue and is not repeated here; session 12's daily
     * sweep is what fires it. Of the ones below only `content_plan.awaiting_sign_off`
     * raises a task — the rest are how `retainers` tells `billing` and the digest that
     * something happened, and `seed/task-rules.ts` `EVENTS_WITHOUT_A_RULE` writes down
     * why for each.
     */
    /** A retainer started; the signup invoice is due. Session 12. */
    'retainer.started',
    /** Staff proposed a package change and the client has to decide. Session 12. */
    'retainer.change_proposed',
    /** The client approved one. It takes effect on its billing day. Session 12. */
    'retainer.change_approved',
    /** The billing run applied one to the retainer. Session 12. */
    'retainer.change_applied',
    /** Cancellation was scheduled; the retainer ends at period end. Session 12. */
    'retainer.ending',
    /** Work is paused: no retainer line while it is. Session 12. */
    'retainer.paused',
    /** Work resumed. Session 12. */
    'retainer.resumed',
    /** Work was delivered against an allowance. Session 12. */
    'deliverable.recorded',
    /** A content plan was sent for sign-off. Session 12. */
    'content_plan.proposed',
    /** A content plan has been waiting five days for a sign-off. Session 12. */
    'content_plan.awaiting_sign_off',
    /*
     * Session 14's two, and the same arrangement as session 11's: neither raises a
     * task. An invoice going out is the billing run's own news, and an invoice being
     * paid is the outcome everybody wanted — a task per one of either would be a board
     * nobody reads. They are here because a domain event is also the record that the
     * thing happened, and because session 15's receipt and session 16's file gating
     * both hang off the second one.
     */
    /** An invoice was issued and sent. Session 14. */
    'invoice.issued',
    /** An invoice settled: the payments against it cover it. Session 14. */
    'invoice.paid',
    /*
     * Session 15's four. Two of the events this session needed were already here:
     * `payment.final_attempt_failed` is `ARCHITECTURE.md` §The task queue's "final
     * charge attempt failed" row and `payment.credit_unmatched` is its "Wise credit
     * unmatched" row, so `.docs/15-billing-payments.md` §6's `charge_exhausted` and
     * `wise_credit_unmatched` are those two under different names and are **not**
     * added again. What was missing is the four below: the failure before the last
     * one, the recovery, the invoice nobody can charge at all, and the top-up that
     * did not go through.
     *
     * Only `invoice.no_payment_method` raises a task. `seed/task-rules.ts`
     * `EVENTS_WITHOUT_A_RULE` says why for the other three, and the short version is
     * that each already sends the client a mail: a task per retry would be a board of
     * things the system is already handling on its own.
     */
    /** An off-session charge was refused, and there are attempts left. Session 15. */
    'payment.charge_failed',
    /** A charge that had failed went through. The retainer comes off past due. Session 15. */
    'payment.charge_recovered',
    /** An auto top-up of a prepaid balance was refused. Session 15. */
    'payment.topup_failed',
    /** An invoice we were meant to charge has no payment method on file. Session 15. */
    'invoice.no_payment_method',
    /*
     * Session 16's five, and the two it did **not** have to add.
     *
     * `report.due_soon` and `deliverable.awaiting_sign_off` are already above: they are
     * `ARCHITECTURE.md` §The task queue's "report due in 7 days" and "deliverable
     * awaiting client sign-off for 5 days" rows, they were written down here in session
     * 07 with "Session 16" beside them, and they each already have a seeded rule. What
     * `.docs/16-files-calls-reports.md` calls `report_due_7d` is the first of those two
     * under another name and is not added again; what it calls `file_review_requested`
     * is a **different** event from the second — asking for a review is the moment, and
     * `deliverable.awaiting_sign_off` is the chase five days later.
     *
     * None of the five raises a task, and `seed/task-rules.ts` `EVENTS_WITHOUT_A_RULE`
     * says why for each. They exist because a domain event is also how one module tells
     * another that something happened — `invoice.paid` is what releases a handover, and
     * `file.released` is what says it happened — and because the record of a booking or
     * an issued report belongs somewhere that is not only a mail.
     */
    /** A held file became downloadable: a handover unlocked, or staff released it. Session 16. */
    'file.released',
    /** A deliverable was sent to the client for review or sign-off. Session 16. */
    'file.review_requested',
    /** A call was booked and is in the calendar. Session 16. */
    'call.booked',
    /** A booked call was cancelled and taken out of the calendar. Session 16. */
    'call.cancelled',
    /** A monthly report was issued and stopped changing. Session 16. */
    'report.issued',
    /**
     * Session 48's four, about a mockup the studio shared for feedback
     * (`.docs/48-mockups.md` §Mail and tasks). Asking raises nothing — the mail is the
     * ask — and each of the client's three moves raises the studio's next piece of work.
     */
    /** A version of a mockup was uploaded and the client asked to review it. Session 48. */
    'mockup.review_requested',
    /** A client member commented on a version of a mockup. Session 48. */
    'mockup.commented',
    /** The client asked for changes on a version of a mockup. Session 48. */
    'mockup.changes_requested',
    /** The client approved a version of a mockup. Session 48. */
    'mockup.approved',
];
export function isDomainEvent(value) {
    return DOMAIN_EVENTS.includes(value);
}
export const DomainEventSchema = z
    .enum(DOMAIN_EVENTS)
    .describe('Something that happened and may raise work: a quote approved, a charge that failed for ' +
    'the last time, a report due. Rules turn one of these into a task.');
/* ------------------------------------------------------------- the payloads */
/**
 * On every payload, and optional on every one.
 *
 * A task raised from an event that is about money carries the money, so the board can
 * print its tag (`mockups/studio/tasks.html`) without joining anything. It is one
 * pair of fields rather than a per-event decision, because "does this event have an
 * amount" is a question about the instance rather than about the kind — an approved
 * work order has one; a report due in a week does not.
 */
const MONEY_TAIL = {
    money: MoneySchema.optional().describe('What this is worth, if anything. Shown as the task’s money tag, and available to a ' +
        'title template as {{money}}, which renders as $1,700.00.'),
    money_ref: z
        .string()
        .optional()
        .describe('The work order, extra or invoice the amount belongs to.'),
};
/**
 * The client this is about, as a slug. Present on eight of the nine.
 *
 * `payment.credit_unmatched` is the exception and has to be: a credit is unmatched
 * precisely because nobody knows whose it is yet, so requiring a client would make
 * the one event that most needs a task impossible to raise.
 */
const CLIENT_FIELD = ClientSlugSchema.describe('The client this happened to, by slug. The task is filed against them.');
/** Session 48's three fields, on each of its four events. */
const MOCKUP_REF_FIELD = z.string().describe('The mockup, MCK-YYYY-NNN.');
const MOCKUP_TITLE_FIELD = z.string().describe('What the mockup is called — "Homepage redesign".');
const MOCKUP_VERSION_FIELD = z.int().describe('Which version, counting from 1.');
/** One payload schema per event. Sessions 11–17 pass exactly these fields. */
export const DOMAIN_EVENT_PAYLOADS = {
    'work_order.approved': z.strictObject({
        client: CLIENT_FIELD,
        work_order_ref: z.string().describe('The project, WO-YYYY-NNN.'),
        title: z.string().describe('What the project is called, for the task’s own title.'),
        ...MONEY_TAIL,
    }),
    'work_order.quote_unanswered': z.strictObject({
        client: CLIENT_FIELD,
        work_order_ref: z.string().describe('The quote, WO-YYYY-NNN.'),
        title: z.string().describe('What was quoted for.'),
        days_waiting: z.int().describe('How many days since it was proposed.'),
        ...MONEY_TAIL,
    }),
    'payment.final_attempt_failed': z.strictObject({
        client: CLIENT_FIELD,
        invoice_ref: z.string().describe('The invoice that could not be charged, FES-YYYY-NNN.'),
        attempts: z.int().describe('How many times it was tried.'),
        ...MONEY_TAIL,
    }),
    'retainer.past_due': z.strictObject({
        client: CLIENT_FIELD,
        days_past_due: z.int().describe('How many days the retainer has been past due.'),
        ...MONEY_TAIL,
    }),
    'report.due_soon': z.strictObject({
        client: CLIENT_FIELD,
        period: PeriodSchema.describe('The month the report covers, YYYY-MM.'),
        due_on: z.string().describe('The day it is due, as it should read in the task — "28 Aug".'),
        ...MONEY_TAIL,
    }),
    'deliverable.awaiting_sign_off': z.strictObject({
        client: CLIENT_FIELD,
        file_id: z.string().describe('The file waiting for sign-off, by id.'),
        name: z.string().describe('What the deliverable is called.'),
        days_waiting: z.int().describe('How many days it has been waiting.'),
        ...MONEY_TAIL,
    }),
    'widget.cap_reached': z.strictObject({
        client: CLIENT_FIELD,
        widget_ref: z.string().describe('The widget that hit its cap, wgt_<type>_<hex4>.'),
        period: PeriodSchema.describe('The month it happened in, YYYY-MM.'),
        ...MONEY_TAIL,
    }),
    'payment.credit_unmatched': z.strictObject({
        client: ClientSlugSchema.optional().describe('The client, if the credit named one. Usually it did not — that is why it is unmatched.'),
        statement_ref: z.string().describe('The Wise statement line, by its id.'),
        received_on: Rfc3339Schema.describe('When the credit landed.'),
        ...MONEY_TAIL,
    }),
    'analytics.effective_rate_below_floor': z.strictObject({
        client: CLIENT_FIELD,
        period: PeriodSchema.describe('The month the rate was computed over, YYYY-MM.'),
        effective_rate: z
            .string()
            .describe('The rate as it should read in the task — "$58/h". Formatted by the caller.'),
        ...MONEY_TAIL,
    }),
    'work_order.proposed': z.strictObject({
        client: CLIENT_FIELD,
        work_order_ref: z.string().describe('The quote, WO-YYYY-NNN or REQ-NNN.'),
        title: z.string().describe('What was quoted for.'),
        valid_until: z
            .string()
            .describe('The last day it can be approved, already formatted — "18 Sep 2026".'),
        ...MONEY_TAIL,
    }),
    'work_order.declined': z.strictObject({
        client: CLIENT_FIELD,
        work_order_ref: z.string().describe('What was declined, WO-YYYY-NNN or REQ-NNN.'),
        title: z.string().describe('What it was for.'),
        note: z.string().describe('Why, in the client’s own words. Empty when they left none.'),
        ...MONEY_TAIL,
    }),
    'work_order.delivered': z.strictObject({
        client: CLIENT_FIELD,
        work_order_ref: z.string().describe('What was delivered, WO-YYYY-NNN or REQ-NNN.'),
        title: z.string().describe('What it was for.'),
        ...MONEY_TAIL,
    }),
    'work_order.approved_project': z.strictObject({
        client: CLIENT_FIELD,
        work_order_ref: z
            .string()
            .describe('The project, WO-YYYY-NNN — or REQ-NNN for a ' +
            'non-retainer extra, which bills as a one-milestone project.'),
        title: z.string().describe('What the project is called.'),
        milestone_seq: z
            .int()
            .describe('Which milestone the deposit is: 1, always. Sent so nothing has to assume it.'),
        ...MONEY_TAIL,
    }),
    'milestone.delivered': z.strictObject({
        client: CLIENT_FIELD,
        work_order_ref: z.string().describe('The project the milestone belongs to.'),
        milestone_seq: z.int().describe('Which milestone, counting from 1.'),
        milestone_title: z.string().describe('What the milestone is called.'),
        final: z
            .boolean()
            .describe('True for the last milestone, whose invoice carries the approved scope changes.'),
        ...MONEY_TAIL,
    }),
    'retainer.started': z.strictObject({
        client: CLIENT_FIELD,
        name: z.string().describe('What the package is called — "Growth+".'),
        started_on: z.string().describe('The signup date, as it should read — "4 Mar 2026".'),
        first_period: PeriodSchema.describe('The first full month, which is what signup bills.'),
        ...MONEY_TAIL,
    }),
    'retainer.change_proposed': z.strictObject({
        client: CLIENT_FIELD,
        change_ref: z.string().describe('The package change, RET-CHG-NNN.'),
        effective_on: z.string().describe('The billing day it would take effect, formatted.'),
        delta: MoneySchema.describe('What it changes the monthly amount by. Negative is less.'),
        ...MONEY_TAIL,
    }),
    'retainer.change_approved': z.strictObject({
        client: CLIENT_FIELD,
        change_ref: z.string().describe('The package change, RET-CHG-NNN.'),
        effective_on: z.string().describe('The billing day it takes effect, formatted.'),
        ...MONEY_TAIL,
    }),
    'retainer.change_applied': z.strictObject({
        client: CLIENT_FIELD,
        change_ref: z.string().describe('The package change, RET-CHG-NNN.'),
        period: PeriodSchema.describe('The period the billing run applied it in, YYYY-MM.'),
        late: z
            .boolean()
            .describe('True when it was applied after its own effective day had already passed.'),
        ...MONEY_TAIL,
    }),
    'retainer.ending': z.strictObject({
        client: CLIENT_FIELD,
        ends_on: z.string().describe('The last day of the last period, formatted.'),
        reason: z.string().describe('Why, in the words whoever scheduled it used.'),
        ...MONEY_TAIL,
    }),
    'retainer.paused': z.strictObject({
        client: CLIENT_FIELD,
        reason: z.string().describe('Why work is paused.'),
        ...MONEY_TAIL,
    }),
    'retainer.resumed': z.strictObject({
        client: CLIENT_FIELD,
        ...MONEY_TAIL,
    }),
    'deliverable.recorded': z.strictObject({
        client: CLIENT_FIELD,
        service: z.string().describe('The rate-card service it was delivered against, by code.'),
        period: PeriodSchema.describe('The period it draws its allowance from, YYYY-MM.'),
        qty: z.number().describe('How many units — articles, pieces, edits, hours.'),
        over: z.number().describe('How much of it was past the allowance. Zero when it was inside.'),
        extra_ref: z
            .string()
            .describe('The extra covering the overage, REQ-NNN. Empty when nothing was over.'),
        ...MONEY_TAIL,
    }),
    'content_plan.proposed': z.strictObject({
        client: CLIENT_FIELD,
        plan_ref: z.string().describe('The content plan, PLAN-YYYY-MM.'),
        period: PeriodSchema.describe('The month it covers, YYYY-MM.'),
        pieces: z.int().describe('How many pieces are in it.'),
        credits: z.int().describe('What they cost against the content allowance.'),
        ...MONEY_TAIL,
    }),
    'content_plan.awaiting_sign_off': z.strictObject({
        client: CLIENT_FIELD,
        plan_ref: z.string().describe('The content plan, PLAN-YYYY-MM.'),
        period: PeriodSchema.describe('The month it covers, YYYY-MM.'),
        days_waiting: z.int().describe('How many days it has been sitting with them.'),
        ...MONEY_TAIL,
    }),
    'invoice.issued': z.strictObject({
        client: CLIENT_FIELD,
        invoice_ref: z.string().describe('The invoice, FES-YYYY-NNN.'),
        kind: z.string().describe('What it is for — monthly, signup, deposit, milestone, one_off.'),
        scope: z
            .string()
            .describe('What it covers, already written out — "Aug 2026", "Website — milestone 2".'),
        due_on: z.string().describe('The day it is due, as it should read — "8 Aug 2026".'),
        ...MONEY_TAIL,
    }),
    'invoice.paid': z.strictObject({
        client: CLIENT_FIELD,
        invoice_ref: z.string().describe('The invoice that settled, FES-YYYY-NNN.'),
        scope: z.string().describe('What it covered, already written out.'),
        paid_on: z.string().describe('The day it settled, as it should read — "6 Aug 2026".'),
        ...MONEY_TAIL,
    }),
    'payment.charge_failed': z.strictObject({
        client: CLIENT_FIELD,
        invoice_ref: z.string().describe('The invoice that could not be charged, FES-YYYY-NNN.'),
        rail: z.string().describe('Which rail it was tried on — ach, sepa or card.'),
        code: z.string().describe('The gateway’s own code — card_declined, R01, AC04.'),
        attempt: z.int().describe('Which attempt this was, counting the first.'),
        attempts_allowed: z.int().describe('How many that rail gets in total (PAYMENTS.md).'),
        retry_on: z
            .string()
            .describe('When the next attempt is due, already formatted. Empty when there is none.'),
        ...MONEY_TAIL,
    }),
    'payment.charge_recovered': z.strictObject({
        client: CLIENT_FIELD,
        invoice_ref: z.string().describe('The invoice that finally went through, FES-YYYY-NNN.'),
        attempts: z.int().describe('How many attempts it took.'),
        ...MONEY_TAIL,
    }),
    'payment.topup_failed': z.strictObject({
        client: CLIENT_FIELD,
        code: z.string().describe('The gateway’s own code, or no_payment_method.'),
        balance: MoneySchema.describe('What is left on the prepaid balance.'),
        ...MONEY_TAIL,
    }),
    'invoice.no_payment_method': z.strictObject({
        client: CLIENT_FIELD,
        invoice_ref: z.string().describe('The invoice waiting to be paid, FES-YYYY-NNN.'),
        due_on: z.string().describe('The day it is due, as it should read — "8 Aug 2026".'),
        ...MONEY_TAIL,
    }),
    'file.released': z.strictObject({
        client: CLIENT_FIELD,
        file_id: z.string().describe('The file that became downloadable, by id.'),
        name: z.string().describe('What it is called, as the client sees it.'),
        kind: z.string().describe('What sort of file — deliverable, handover, report_pdf.'),
        reason: z
            .string()
            .describe('Why it was released — "final invoice paid", or the reason a person typed for an ' +
            'override.'),
        ...MONEY_TAIL,
    }),
    'file.review_requested': z.strictObject({
        client: CLIENT_FIELD,
        file_id: z.string().describe('The deliverable waiting for an answer, by id.'),
        name: z.string().describe('What it is called, as the client sees it.'),
        requested_on: z
            .string()
            .describe('The day it was sent for review, as it should read — "24 Aug 2026".'),
        ...MONEY_TAIL,
    }),
    'call.booked': z.strictObject({
        client: CLIENT_FIELD,
        call_ref: z.string().describe('The call, CALL-YYYY-NNN.'),
        call_type: z.string().describe('What kind of call — "Monthly review", "Quick question".'),
        when: z
            .string()
            .describe('When it starts, already formatted in the studio’s zone — "Thu 3 Sep, 15:00".'),
        minutes: z.int().describe('How long it is booked for.'),
        ...MONEY_TAIL,
    }),
    'call.cancelled': z.strictObject({
        client: CLIENT_FIELD,
        call_ref: z.string().describe('The call that was cancelled, CALL-YYYY-NNN.'),
        call_type: z.string().describe('What kind of call it was.'),
        when: z.string().describe('When it was going to be, already formatted.'),
        reason: z.string().describe('Why, in the words whoever cancelled it used. Empty when none.'),
        ...MONEY_TAIL,
    }),
    'report.issued': z.strictObject({
        client: CLIENT_FIELD,
        period: PeriodSchema.describe('The month the report covers, YYYY-MM.'),
        headline: z.string().describe('The archive’s one-line summary of the month.'),
        issued_on: z.string().describe('The day it was issued, as it should read — "28 Aug 2026".'),
        ...MONEY_TAIL,
    }),
    'mockup.review_requested': z.strictObject({
        client: CLIENT_FIELD,
        mockup_ref: MOCKUP_REF_FIELD,
        title: MOCKUP_TITLE_FIELD,
        version: MOCKUP_VERSION_FIELD,
        requested_on: z
            .string()
            .describe('The day it was sent for review, as it should read — "28 Oct 2026".'),
        ...MONEY_TAIL,
    }),
    'mockup.commented': z.strictObject({
        client: CLIENT_FIELD,
        mockup_ref: MOCKUP_REF_FIELD,
        title: MOCKUP_TITLE_FIELD,
        version: MOCKUP_VERSION_FIELD,
        author: z.string().describe('Who commented, by name.'),
        ...MONEY_TAIL,
    }),
    'mockup.changes_requested': z.strictObject({
        client: CLIENT_FIELD,
        mockup_ref: MOCKUP_REF_FIELD,
        title: MOCKUP_TITLE_FIELD,
        version: MOCKUP_VERSION_FIELD,
        author: z.string().describe('Who answered, by name.'),
        said: z.string().describe('What they wrote with the answer, or "nothing beyond the comments".'),
        ...MONEY_TAIL,
    }),
    'mockup.approved': z.strictObject({
        client: CLIENT_FIELD,
        mockup_ref: MOCKUP_REF_FIELD,
        title: MOCKUP_TITLE_FIELD,
        version: MOCKUP_VERSION_FIELD,
        author: z.string().describe('Who answered, by name.'),
        said: z.string().describe('What they wrote with the answer, or "nothing beyond the comments".'),
        ...MONEY_TAIL,
    }),
};
/** The schema for one event's payload. The only way to get one. */
export function eventPayloadSchema(event) {
    return DOMAIN_EVENT_PAYLOADS[event];
}
/**
 * The field names one event's payload carries, in declaration order.
 *
 * What a rule's templates may refer to. `seed/task-rules.ts` is checked against this,
 * so a seeded rule whose title names a field its event does not have is a failing
 * test rather than a `{{placeholder}}` in somebody's inbox.
 */
export function eventPayloadFields(event) {
    return Object.keys(DOMAIN_EVENT_PAYLOADS[event].shape);
}
//# sourceMappingURL=events.js.map